import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {Chart} from 'angular-highcharts';
import {PointOptionsObject, SeriesOptionsType} from 'highcharts';
import {RecycleChartData} from '../../../../models/recycle-chart-data.interface';
import {COMPOSITION_COLORS} from '../../../../../constants/charts';
import {MessageService} from '../../../../../shared/services/message.service';
import {Router} from '@angular/router';
import {AuthService} from '../../../../../core/services/auth.service';
import {formatNumber} from '../../../../utils/number-format-utils';


@Component({
  selector: 'app-recycle-bar-chart',
  templateUrl: './recycle-bar-chart.component.html',
  styleUrls: ['./recycle-bar-chart.component.scss']
})
export class RecycleBarChartComponent implements OnInit, OnChanges {

  textBelow!: string;
  chart!: Chart;
  chartData!: SeriesOptionsType[];
  csCodes!: string[];
  textBelowCode = 'kfcharts.WeeeSystem.Recycle.ByCollectionStream.textBelow';
  isAdmin!: boolean;

  @Input()
  data!: RecycleChartData[];
  @Input()
  displayUnits!: string;
  @Input()
  title!: string;
  @Input()
  subtitle!: string;

  constructor(private readonly messageService: MessageService,
              private readonly router: Router,
              private authService: AuthService) {
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.getUserRoles().includes('WF_ADMIN');
    this.messageService.getDialogInfo(this.textBelowCode).subscribe((data) => {
      if (data !== null) {
        this.textBelow = data.description;
      }
    });
  }

  ngOnChanges(): void {
    if (this.data) {
      this.csCodes = this.data.map(item => item.collectionStream);
      this.chartData = this.convertToChartData(this.data);
      this.chart = new Chart({
        chart: {
          zoomType: 'xy',
          type: 'column',
          options3d: {
            enabled: true,
            alpha: 7,
            beta: 0,
            depth: 35,
            viewDistance: 15
          }
        },
        colors: COMPOSITION_COLORS,
        title: {
          text: this.title
        },
        subtitle: {
          text: this.subtitle
        },
        exporting: {
          tableCaption: this.title + ' ' + this.subtitle
        },
        plotOptions: {
          column: {
            stacking: 'normal',
          },
        },
        xAxis: {
          categories: this.csCodes,
          title: {
            text: null
          }
        },
        yAxis: {
          labels: {
            format: '{value}%'
          }
        },
        tooltip: {
          formatter(): string[] | undefined {
            const pointOptions = this.point as PointOptionsObject;
            if (this.point.name) {
              if(this.point.y == 0) {
                return['<div class="center"><b>' + this.point.name + '</b></div><br><br>'];
              }
              return ['<div class="center"><b>' + this.point.name + '</b></div><br><br>',
                `- ${pointOptions.custom?.category}: ${formatNumber(this.point.y)}  %<br>`,
              ];
            }
            return undefined;
          }
        },
        series: this.chartData
      });
    }
  }

  editTextBelow(): void {
    this.router.navigate(
      ['/admin/chart-labels'], {state: {code: this.textBelowCode}}).then(
      () => {
        // do nothing
      }
    );
  }

  private convertToChartData(data: RecycleChartData[]): SeriesOptionsType[] {
    this.chart?.ref?.hideData();
    return [{
      name: 'Material Recovery',
      type: 'column',
      data: data.map(item => {
        return {
          y: (this.displayUnits === 'AVERAGE' ? item.averageMR : item.weightedAverageMR) == null ? 0 :
            Number((this.displayUnits === 'AVERAGE' ? item.averageMR : item.weightedAverageMR)?.toFixed(2)),
          name: item.collectionStream,
          custom: {collectionStream: item.collectionStream, category: 'Material Recovery'}
        };
      })
    }, {
      name: 'Re-use', type: 'column',
      data: data.map(item => {
        return {
          y: (this.displayUnits === 'AVERAGE' ? item.averageRU : item.weightedAverageRU) == null ? 0 :
            Number((this.displayUnits === 'AVERAGE' ? item.averageRU : item.weightedAverageRU)?.toFixed(2)),
          name: item.collectionStream,
          custom: {collectionStream: item.collectionStream, category: 'Re-use'}
        };
      })
    }, {
      name: 'Energy Recovery', type: 'column',
      data: data.map(item => {
        return {
          y: (this.displayUnits === 'AVERAGE' ? item.averageER : item.weightedAverageER) == null ? 0 :
            Number((this.displayUnits === 'AVERAGE' ? item.averageER : item.weightedAverageER)?.toFixed(2)),
          name: item.collectionStream,
          custom: {collectionStream: item.collectionStream, category: 'Energy Recovery'}
        };
      })
    }, {
      name: 'Thermal Disposal', type: 'column',
      data: data.map(item => {
        return {
          y: (this.displayUnits === 'AVERAGE' ? item.averageTD : item.weightedAverageTD) == null ? 0 :
            Number((this.displayUnits === 'AVERAGE' ? item.averageTD : item.weightedAverageTD)?.toFixed(2)),
          name: item.collectionStream,
          custom: {collectionStream: item.collectionStream, category: 'Thermal Disposal'}
        };
      })
    }, {
      name: 'Landfill', type: 'column',
      data: data.map(item => {
        return {
          y: (this.displayUnits === 'AVERAGE' ? item.averageLD : item.weightedAverageLD) == null ? 0 :
            Number((this.displayUnits === 'AVERAGE' ? item.averageLD : item.weightedAverageLD)?.toFixed(2)),
          name: item.collectionStream,
          custom: {collectionStream: item.collectionStream, category: 'Landfill'}
        };
      })
    }, {
      name: 'Loss', type: 'column',
      data: data.map(item => {
        return {
          y: (this.displayUnits === 'AVERAGE' ? item.averageLoss : item.weightedAverageLoss) == null ? 0 :
              Number((this.displayUnits === 'AVERAGE' ? item.averageLoss : item.weightedAverageLoss).toFixed(2)),
          name: item.collectionStream,
          custom: {collectionStream: item.collectionStream, category: 'Loss'}
        };
      })
    }, {
      name: 'Other Material Recycling', type: 'column',
      data: data.map(item => {
        return {
          y: (this.displayUnits === 'AVERAGE' ? item.averageOMR : item.weightedAverageOMR) == null ? 0 :
            Number((this.displayUnits === 'AVERAGE' ? item.averageOMR : item.weightedAverageOMR)?.toFixed(2)),
          name: item.collectionStream,
          custom: {collectionStream: item.collectionStream, category: 'OMR'}
        };
      })
    }];
  }

}
